/**
 * Pure helpers for the Cabinet Builder.
 *
 * Nothing here touches React state, so each function can be reasoned about and
 * tested on its own.
 */

export type CalculatedHardware = {
	hardwareMaterials: Array<Record<string, any>>;
	cabinetFixtures: Array<Record<string, any>>;
	hardwareConnectors: Array<Record<string, any>>;
	handles: Record<string, any> | null;
	handleCap: Record<string, any> | null;
	kitchenSink: Record<string, any> | null;
	faucet: Record<string, any> | null;
	handlesLength: number | null;
	shutterCount: number | null;
	hingeCount: number | null;
};

export const DRAWER_SHUTTER_MODES = ['DRAWER_2', 'DRAWER_3'] as const;

export const BASE_SINK_BC_BUILDER_CODE = 'CBBUILDERBASESINKBCUNIT';

export const isBaseSinkBcBuilder = (builder?: unknown) =>
	String(builder ?? '').toUpperCase().trim() === BASE_SINK_BC_BUILDER_CODE;

const isSidePanelCutlistItem = (item: Record<string, any>) => {
	const name = String(item?.name ?? item?.pieceName ?? item?.piece_name ?? '').toLowerCase();
	return name.includes('side panel');
};

const withFullHeight = (item: Record<string, any>, height: number) => ({
	...item,
	finishingHeight: height,
	cuttingHeight: Math.max(0, height - 2),
	finishing_height: height,
	cutting_height: Math.max(0, height - 2)
});

export const normalizeSinkBcSidePanelHeights = (
	cutlist: Array<Record<string, any>> = [],
	builder: unknown,
	height: unknown
) => {
	const fullHeight = Number(height);
	if (!isBaseSinkBcBuilder(builder) || !Number.isFinite(fullHeight) || fullHeight <= 0) return cutlist;

	return cutlist.map(item => {
		if (!item || typeof item !== 'object' || !isSidePanelCutlistItem(item)) return item;
		return withFullHeight(item, fullHeight);
	});
};

// ── Key casing ─────────────────────────────────────────────────────────────
// The costing API answers in snake_case; the form works in camelCase.

export const toCamelCase = (str: string) => str.replace(/_([a-z0-9])/g, (_match, c) => String(c).toUpperCase());

export const camelizeKeysDeep = (input: any): any => {
	if (Array.isArray(input)) return input.map(camelizeKeysDeep);
	if (input && typeof input === 'object') {
		return Object.fromEntries(Object.entries(input).map(([key, value]) => [toCamelCase(key), camelizeKeysDeep(value)]));
	}
	return input;
};

// ── Material lookup ────────────────────────────────────────────────────────

export const normalizeText = (value?: unknown) => String(value ?? '').toLowerCase();

export const materialText = (item: any) =>
	`${normalizeText(item?.code)} ${normalizeText(item?.name)} ${normalizeText(item?.description)}`;

/** Keeps materials whose code, name, or description contains any keyword. */
export const filterByKeywords = (items: any[], keywords: string[]) => {
	const list = Array.isArray(items) ? items : [];
	const keys = (keywords || []).map(key => normalizeText(key)).filter(Boolean);
	if (keys.length === 0) return list;
	return list.filter(item => keys.some(key => materialText(item).includes(key)));
};

/**
 * Resolves a preset's material reference to the full material object.
 * Falls back to the original value when the code is not in `options`, so a preset
 * referencing a withdrawn material still shows what it asked for.
 */
export const resolveSfbMaterial = (value: any, options: Array<Record<string, any>>) => {
	if (!value) return null;
	const code = typeof value === 'string' ? value : value?.code ?? value?.materialCode;
	if (!code) return typeof value === 'object' ? value : null;
	return (Array.isArray(options) ? options : []).find(material => material.code === code) ?? value;
};

// ── Units and display ──────────────────────────────────────────────────────

export const sqftToMm = (sqft: number) => sqft * 92903.04;
export const mmToSqft = (mm: number) => mm / 92903.04;

export const formatLength = (length: unknown) => {
	const numericLength = Number(length);
	if (Number.isFinite(numericLength) && numericLength > 0) return `${numericLength} mm`;
	return 'Length unavailable';
};

/** Returns null for anything that is not a usable count, so callers can hide the stat. */
export const formatCount = (count: unknown) => {
	const numericCount = Number(count);
	if (Number.isFinite(numericCount) && numericCount >= 0) return numericCount;
	return null;
};

export const MAX_CABINET_SHELVES = 8;

export const shouldShowCabinetShelfCountInput = (formData: {
	shutterMode?: string | null;
	builder?: string | null;
	productName?: string | null;
}): boolean => {
	const mode = String(formData.shutterMode ?? '').toUpperCase();
	if (mode === 'DRAWER_2' || mode === 'DRAWER_3') return false;
	const builder = String(formData.builder ?? '').toUpperCase();
	const name = String(formData.productName ?? '').toUpperCase();
	const shelfBlockingTokens = ['HOB', 'SINK', 'GTPT', 'GTP', 'BPO', 'ROUNIT'];
	if (shelfBlockingTokens.some(token => builder.includes(token))) return false;
	if (shelfBlockingTokens.some(token => name.includes(token)) || name.includes('RO UNIT')) return false;
	return true;
};

export const getDefaultCabinetShelfCount = (formData: {
	height?: unknown;
	builder?: string | null;
	configuration?: string | null;
	cabinetType?: string | null;
}): number => {
	if (String(formData.cabinetType ?? '').toUpperCase() === 'L_CORNER') return 0;
	const builder = String(formData.builder ?? '').toUpperCase();
	if (['CBBUILDERTALLUNIT', 'CBBUILDERBASEPANTRY', 'TU', 'PU'].includes(builder)) {
		const configuration = String(formData.configuration ?? '');
		if (!configuration) return 3;
		if (configuration === '1Ap 2S' || configuration === '1Ap 1S 1OU') return 2;
		if (configuration === '2Ap 1S 1OU' || configuration === '2Ap 2S') return 3;
		return 0;
	}
	return Number(formData.height) > 700 ? 2 : 1;
};

/** Null/empty means use the height/config default. 0 is an explicit empty carcass. */
export const resolveAuthoredCabinetShelfCount = (value: unknown): number | null => {
	if (value === null || value === undefined || value === '') return null;
	const numericCount = Number(value);
	if (!Number.isFinite(numericCount) || numericCount < 0) return null;
	return Math.min(MAX_CABINET_SHELVES, Math.round(numericCount));
};
